import React from 'react';

import { alertSuccess } from '@dashboard/contexts/SnackbarManager/store';
import {
  type CurrentUserQuery,
  type FPlatformUserFragment,
  PlatformUserRole,
  PlatformUserStatus,
  useCurrentPlatformUserQuery,
  useCurrentUserQuery,
  useUpdatePlatformUserRoleMutation,
} from '@dashboard/generated/wp/authentication';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';

import useMembersQuery from './useMembersQuery';

type Props = {
  platformUser: FPlatformUserFragment;
};

export default function PlatformUserRoleMenu({ platformUser }: Props) {
  const platformId = useCurrentPlatformId();
  const { data: currentUserData } = useCurrentUserQuery();
  const { data: currentPlatformUserData } = useCurrentPlatformUserQuery({ variables: { platformId } });

  if (currentUserData === undefined || currentPlatformUserData === undefined) {
    return null;
  }

  const currentPlatformUser = currentPlatformUserData.currentPlatformUser;
  const currentUser = currentUserData.currentUser!;

  const isUpdateBlocked = isUpdatePermissionsBlocked({
    platformUser,
    currentUser,
    currentPlatformUser,
  });

  if (isUpdateBlocked) {
    return <Typography color="text.secondary">{getRoleName(platformUser)}</Typography>;
  }

  return (
    <DropdownRole
      platformUser={platformUser}
      includeOwner={
        platformUser.status === PlatformUserStatus.Accepted &&
        (currentUser.role === 'ADMIN' || currentPlatformUser.role === PlatformUserRole.Owner)
      }
    />
  );
}

function DropdownRole({ platformUser, includeOwner }: { platformUser: FPlatformUserFragment; includeOwner: boolean }) {
  const platformId = useCurrentPlatformId();
  const [update, { loading }] = useUpdatePlatformUserRoleMutation();
  const { refetch } = useMembersQuery();

  const handleChange = async (event: SelectChangeEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const role = event.target.value as any;
    await update({ variables: { platformUserId: platformUser.id, platformId, role } });
    await refetch();
    alertSuccess('Updated team member role');
  };

  return (
    <Select disabled={loading} value={platformUser.role} onChange={handleChange} size="small">
      <MenuItem value={PlatformUserRole.Owner} disabled={!includeOwner}>
        Owner
      </MenuItem>
      <MenuItem value={PlatformUserRole.Admin}>Admin</MenuItem>
      <MenuItem value={PlatformUserRole.User}>User</MenuItem>
    </Select>
  );
}

type UpdatePermissionsBlockedProps = {
  platformUser: FPlatformUserFragment;
  currentPlatformUser: FPlatformUserFragment;
  currentUser: NonNullable<CurrentUserQuery['currentUser']>;
};
function isUpdatePermissionsBlocked({ currentUser, platformUser, currentPlatformUser }: UpdatePermissionsBlockedProps) {
  // Waypoint ADMIN
  if (currentUser.role === 'ADMIN') {
    return false;
  }

  // Current user role is USER
  if (currentPlatformUser.role === PlatformUserRole.User) {
    return true;
  }
  // Cannot change your own role
  if (currentPlatformUser.id === platformUser.id) {
    return true;
  }
  // A non-ADMIN user cannot change the role of the owner
  if (currentPlatformUser.role !== PlatformUserRole.Owner && platformUser.role === PlatformUserRole.Owner) {
    return true;
  }

  return false;
}

function getRoleName(platformUser: FPlatformUserFragment) {
  switch (platformUser.role) {
    case PlatformUserRole.Owner:
      return 'Owner';
    case PlatformUserRole.Admin:
      return 'Admin';
    case PlatformUserRole.User:
      return 'User';
  }
}
